import { Component, Input, OnInit, OnChanges } from '@angular/core';
import { ChartType, ChartDataSets, ChartOptions } from 'chart.js';
import { Label } from 'ng2-charts';
import * as ChartConfig from './campaign-graphical-analytics-chart.config';

@Component({
    selector: 'app-campaign-graphical-analytics',
    templateUrl: './campaign-graphical-analytics.component.html',
    styleUrls: ['./campaign-graphical-analytics.component.scss']
})
export class CampaignGraphicalAnalyticsComponent implements OnInit, OnChanges {
    @Input() estimatedFactorAndSpentOverTime: any = [];
    @Input() factorDistributionAcrossChannel: any = [];
    @Input() loading = false;
    @Input() allChannelsSelected: boolean = true;
    @Input() isActualMode: boolean = false;
    @Input() isDataAvailable: boolean = true;

    public comboChartType: ChartType = 'bar';
    public comboChartLabels: Label[] = [];
    public comboChartData: ChartDataSets[] = [];
    public comboChartOptions: ChartOptions = ChartConfig.comboChartOptions;

    public pieChartType: ChartType = 'pie';
    public pieChartLabels: Label[] = [];
    public pieChartData: number[] = [];
    public pieChartColors = [{ backgroundColor: [] }];
    public pieChartOptions: ChartOptions = ChartConfig.pieChartOptions;

    constructor() {}

    ngOnInit(): void {
        if (!this.loading && this.estimatedFactorAndSpentOverTime?.dates?.length > 0) {
            this.fetchChartData();
        }
    }

    ngOnChanges(): void {
        if (!this.loading && this.estimatedFactorAndSpentOverTime?.dates?.length > 0) {
            this.fetchChartData();
        }
    }

    private async fetchChartData(): Promise<void> {
        const [performanceData, channelData] = await Promise.all([
            this.fetchPerformanceData(),
            this.fetchChannelData()
        ]);

        const prefix = this.isActualMode ? 'Actual ' : 'Estimated ';

        this.comboChartLabels = performanceData.labels;
        this.comboChartData = [
            {
                type: 'bar',
                label: prefix + this.estimatedFactorAndSpentOverTime.factorType,
                data: performanceData.plays,
                backgroundColor: 'rgba(255, 193, 7, 0.8)',
                borderColor: 'rgba(255, 193, 7, 1)',
                borderWidth: 1,
                yAxisID: 'y-axis-0',
                order: 2
            },
            {
                type: 'line',
                label: 'Spent',
                data: performanceData.spent,
                borderColor: 'rgba(244, 67, 54, 1)',
                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                borderWidth: 2,
                borderDash: [5, 5],
                fill: false,
                yAxisID: 'y-axis-1',
                order: 1
            }
        ];

        this.pieChartLabels = [...channelData.labels];
        this.pieChartData = [...channelData.values];

        const baseOptions = JSON.parse(JSON.stringify(ChartConfig.pieChartOptions));
        const specificTitle = this.factorDistributionAcrossChannel.factorType + ' Distribution Across Channels';

        if (this.isActualMode) {
            this.pieChartOptions = {
                ...baseOptions,
                title: {
                    ...baseOptions.title,
                    display: true,
                    text: specificTitle
                },
                plugins: {
                    datalabels: {
                        display: true,
                        formatter: (value, context) => {
                            const percentage = this.factorDistributionAcrossChannel.factorPercentages[context.dataIndex];
                            return `${percentage}%`;
                        },
                        anchor: 'center',
                        align: 'center'
                    }
                },
                tooltips: {
                    callbacks: {
                        label: (tooltipItem, data) => {
                            const label = data.labels[tooltipItem.index] || '';
                            const percentage = this.factorDistributionAcrossChannel.factorPercentages[tooltipItem.index];
                            return `${label}: ${percentage}%`;
                        }
                    }
                }
            };
        } else {
            this.pieChartOptions = ChartConfig.pieChartOptions;
        }

        this.pieChartColors = [
            {
                backgroundColor: ChartConfig.generateColors(channelData.values.length)
            }
        ];
    }

    private fetchPerformanceData(): Promise<{
        labels: Label[];
        plays: number[];
        spent: number[];
    }> {
        return new Promise(resolve => {
            const labels: Label[] = this.estimatedFactorAndSpentOverTime.dates;
            const plays: number[] = this.estimatedFactorAndSpentOverTime.factorValue;
            const spent: number[] = this.estimatedFactorAndSpentOverTime.spentOverTime;
            const prefix = this.isActualMode ? 'Actual ' : 'Estimated ';

            this.comboChartOptions.title.text =
                prefix +
                this.estimatedFactorAndSpentOverTime.factorType +
                ' And Spent Balance Over Time';

            resolve({
                labels,
                plays,
                spent
            });
        });
    }

    private fetchChannelData(): Promise<{ labels: Label[]; values: number[] }> {
        return new Promise(resolve => {
            this.pieChartOptions.title.text =
                this.factorDistributionAcrossChannel.factorType + ' Distribution Across Channels';

            resolve({
                labels: this.factorDistributionAcrossChannel.channelNames,
                values: this.factorDistributionAcrossChannel.factorValue
            });
        });
    }
}
